import React, { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import './ColumnFilter.css';

const FilterIcon = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden>
    <polygon points="22 3 2 3 10 12.46 10 19 14 21 14 12.46 22 3" />
  </svg>
);

interface ColumnFilterProps {
  active: boolean;
  ariaLabel: string;
  children: React.ReactNode;
  onApply: () => void;
  wide?: boolean;
}

export const ColumnFilter: React.FC<ColumnFilterProps> = ({
  active,
  ariaLabel,
  children,
  onApply,
  wide = false,
}) => {
  const wrapRef = useRef<HTMLDivElement>(null);
  const popoverRef = useRef<HTMLDivElement>(null);
  const [open, setOpen] = useState(false);
  const [coords, setCoords] = useState({ top: 0, left: 0 });

  useEffect(() => {
    if (!open || !wrapRef.current) return;
    const rect = wrapRef.current.getBoundingClientRect();
    const width = wide ? 240 : 180;
    const left = Math.max(8, Math.min(rect.right - width, window.innerWidth - width - 8));
    setCoords({ top: rect.bottom + 4, left });
  }, [open, wide]);

  useEffect(() => {
    if (!open) return;
    const onDoc = (event: MouseEvent) => {
      const target = event.target as Node;
      if (wrapRef.current?.contains(target) || popoverRef.current?.contains(target)) return;
      setOpen(false);
    };
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, [open]);

  return (
    <div className="column-filter" ref={wrapRef}>
      <button
        type="button"
        className={`column-filter__icon${active ? ' column-filter__icon--active' : ''}`}
        onClick={() => setOpen((value) => !value)}
        aria-label={ariaLabel}
        aria-expanded={open}
        title={ariaLabel}
      >
        <FilterIcon />
      </button>
      {open &&
        createPortal(
          <div
            ref={popoverRef}
            className={`column-filter__popover${wide ? ' column-filter__popover--wide' : ''}`}
            style={{ top: coords.top, left: coords.left }}
          >
            {children}
            <div className="column-filter__actions">
              <button
                type="button"
                className="column-filter__apply"
                onClick={() => {
                  onApply();
                  setOpen(false);
                }}
              >
                Apply
              </button>
            </div>
          </div>,
          document.body
        )}
    </div>
  );
};

export interface FilterOption {
  value: string;
  label: string;
}

interface MultiSelectFilterProps {
  options: FilterOption[];
  value: string[];
  onApply: (next: string[]) => void;
  ariaLabel: string;
}

export const MultiSelectFilter: React.FC<MultiSelectFilterProps> = ({
  options,
  value,
  onApply,
  ariaLabel,
}) => {
  const [draft, setDraft] = useState(value);
  useEffect(() => setDraft(value), [value]);

  return (
    <ColumnFilter
      active={value.length > 0}
      ariaLabel={ariaLabel}
      onApply={() => onApply(draft)}
    >
      <div className="column-filter__title">{ariaLabel}</div>
      <div className="column-filter__options">
        {options.map((option) => {
          const checked = draft.includes(option.value);
          return (
            <label key={option.value} className="column-filter__option">
              <input
                type="checkbox"
                checked={checked}
                onChange={() => {
                  setDraft((current) =>
                    current.includes(option.value)
                      ? current.filter((item) => item !== option.value)
                      : [...current, option.value]
                  );
                }}
              />
              {option.label}
            </label>
          );
        })}
      </div>
    </ColumnFilter>
  );
};

interface NumberRangeFilterProps {
  min: string;
  max: string;
  onApply: (next: { min: string; max: string }) => void;
  ariaLabel: string;
}

export const NumberRangeFilter: React.FC<NumberRangeFilterProps> = ({
  min,
  max,
  onApply,
  ariaLabel,
}) => {
  const [draftMin, setDraftMin] = useState(min);
  const [draftMax, setDraftMax] = useState(max);
  useEffect(() => {
    setDraftMin(min);
    setDraftMax(max);
  }, [min, max]);

  return (
    <ColumnFilter
      active={Boolean(min || max)}
      ariaLabel={ariaLabel}
      onApply={() => onApply({ min: draftMin, max: draftMax })}
    >
      <div className="column-filter__title">Score range</div>
      <div className="column-filter__row">
        <input
          type="number"
          min={0}
          max={10}
          step={0.1}
          placeholder="Min"
          className="column-filter__input"
          value={draftMin}
          onChange={(event) => setDraftMin(event.target.value)}
        />
        <span className="column-filter__sep">–</span>
        <input
          type="number"
          min={0}
          max={10}
          step={0.1}
          placeholder="Max"
          className="column-filter__input"
          value={draftMax}
          onChange={(event) => setDraftMax(event.target.value)}
        />
      </div>
    </ColumnFilter>
  );
};

interface DateTimeRangeFilterProps {
  fromDate: string;
  fromTime: string;
  toDate: string;
  toTime: string;
  onApply: (next: {
    fromDate: string;
    fromTime: string;
    toDate: string;
    toTime: string;
  }) => void;
  ariaLabel: string;
}

export const DateTimeRangeFilter: React.FC<DateTimeRangeFilterProps> = ({
  fromDate,
  fromTime,
  toDate,
  toTime,
  onApply,
  ariaLabel,
}) => {
  const [draftFromDate, setDraftFromDate] = useState(fromDate);
  const [draftFromTime, setDraftFromTime] = useState(fromTime);
  const [draftToDate, setDraftToDate] = useState(toDate);
  const [draftToTime, setDraftToTime] = useState(toTime);
  useEffect(() => {
    setDraftFromDate(fromDate);
    setDraftFromTime(fromTime);
    setDraftToDate(toDate);
    setDraftToTime(toTime);
  }, [fromDate, fromTime, toDate, toTime]);

  return (
    <ColumnFilter
      active={Boolean(fromDate || toDate)}
      ariaLabel={ariaLabel}
      wide
      onApply={() =>
        onApply({
          fromDate: draftFromDate,
          fromTime: draftFromTime,
          toDate: draftToDate,
          toTime: draftToTime,
        })
      }
    >
      <div className="column-filter__title">From</div>
      <div className="column-filter__row">
        <input
          type="date"
          className="column-filter__input"
          value={draftFromDate}
          onChange={(event) => setDraftFromDate(event.target.value)}
        />
        <input
          type="time"
          className="column-filter__input column-filter__input--time"
          value={draftFromTime}
          onChange={(event) => setDraftFromTime(event.target.value)}
        />
      </div>
      <div className="column-filter__title">To</div>
      <div className="column-filter__row">
        <input
          type="date"
          className="column-filter__input"
          value={draftToDate}
          onChange={(event) => setDraftToDate(event.target.value)}
        />
        <input
          type="time"
          className="column-filter__input column-filter__input--time"
          value={draftToTime}
          onChange={(event) => setDraftToTime(event.target.value)}
        />
      </div>
    </ColumnFilter>
  );
};
